import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  buildDerivedParams,
  buildSearchParams,
  buildWordCloudParams,
  getChannels,
} from "../analyzeUtils";
import { dataSourceHandleActions, messageActions } from "_actions";
import useLocationQueryParams from "components/v2/hooks/useLocationQueryParams";

export default function useAnalyze() {
  const ROWS_PER_PAGE = 10;
  const currentUser = JSON.parse(localStorage.user);
  const organizationTrendsId = currentUser?._embedded?.organization?.trendsId;
  const { params, setParams } = useLocationQueryParams();
  const dispatch = useDispatch();
  const exporting = useSelector((state) => state.search?.exporting);

  const dataSourceHandles = useSelector(
    (state) => state.dataSourceHandles.dataSourceHandles,
  );
  const { wordCloud, loadingWordCloud } = useSelector((state) => state.search);
  const initialized = useRef(false);

  const derivedParams = useMemo(() => {
    return buildDerivedParams(params);
  }, [params]);

  const [searchInput, setSearchInput] = useState(() => {
    return derivedParams.searchString;
  });

  const [filterParams, setFilterParams] = useState(() => {
    return {
      startDate: derivedParams.startDate.format("YYYY-MM-DD HH:mm:ss"),
      endDate: derivedParams.endDate.format("YYYY-MM-DD HH:mm:ss"),
      organizations: [organizationTrendsId],
      channels: derivedParams.channels,
      offset: derivedParams.offset,
      direction: derivedParams.direction,
      page: derivedParams.page,
      view: derivedParams.view || "wordcloud",
    };
  });

  const { channelsList, channelIds } = useMemo(() => {
    if (!dataSourceHandles) return { channelsList: [], channelIds: [] };

    return {
      channelsList: getChannels(dataSourceHandles),
      channelIds: getChannels(dataSourceHandles, "ids"),
    };
  }, [dataSourceHandles]);

  const handleSearchField = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const fetchWordCloudData = useCallback(() => {
    const params = buildWordCloudParams({
      channelsId: filterParams.channels,
      dataSourceHandles,
      startDate: moment(filterParams.startDate).startOf("day").toISOString(),
      endDate: moment(filterParams.endDate).endOf("day").toISOString(),
      orgTrendsId: organizationTrendsId,
      direction: filterParams.direction,
    });

    if (params) {
      dispatch(messageActions.getWordCloudData(params));
      const nextFilters = {
        ...params,
        page: 0,
        offset: 0,
        view: "wordcloud",
      };
      setFilterParams(nextFilters);
      setParams(nextFilters);
    }
  }, [
    channelIds,
    dataSourceHandles,
    filterParams,
    organizationTrendsId,
    dispatch,
  ]);

  const fetchInitialWordCloudData = useCallback(() => {
    const params = buildWordCloudParams({
      channelsId: filterParams.channels,
      dataSourceHandles,
      startDate: derivedParams.startDate.startOf("day").toISOString(),
      endDate: derivedParams.endDate.endOf("day").toISOString(),
      orgTrendsId: organizationTrendsId,
      direction: derivedParams.direction,
    });

    if (params) {
      dispatch(messageActions.getWordCloudData(params));
    }
  }, [
    channelIds,
    dataSourceHandles,
    organizationTrendsId,
    dispatch,
    derivedParams,
  ]);

  const handleSearch = useCallback(
    (searchString) => {
      const trimmed = searchString?.trim();
      if (!trimmed) return;

      const params = buildSearchParams({
        filters: filterParams,
        searchInput: trimmed,
        dataSourceHandles: dataSourceHandles,
        orgTrendsId: organizationTrendsId,
      });

      if (params) {
        dispatch(messageActions.clearVisitedPages());
        dispatch(messageActions.searchMessages(params));
        dispatch(messageActions.getSearchSummary(params));

        const nextFilters = {
          ...params,
          page: 0,
          offset: 0,
          view: "search",
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
        };
        setFilterParams(nextFilters);
        setParams(nextFilters);
      }
    },
    [filterParams, dataSourceHandles],
  );

  const handleSubmit = useCallback(() => {
    if (searchInput) {
      handleSearch(searchInput);
    } else {
      fetchWordCloudData();
    }
  }, [fetchWordCloudData, handleSearch, searchInput]);

  const handleSearchOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleWordCloudWordClick = useCallback(
    (word) => {
      setSearchInput(word);
      handleSearch(word);
    },
    [handleSearch],
  );

  const handlePageChange = useCallback(
    (e, nextPage) => {
      const nextOffset = (nextPage || 0) * ROWS_PER_PAGE;

      const updatedFilters = {
        ...filterParams,
        page: nextPage,
        offset: nextOffset,
      };

      const params = buildSearchParams({
        filters: updatedFilters,
        searchInput: searchInput,
        dataSourceHandles: dataSourceHandles,
        orgTrendsId: organizationTrendsId,
      });

      if (params) {
        dispatch(messageActions.searchMessages(params));

        const nextFilters = {
          ...params,
          view: "search",
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
        };
        setFilterParams(nextFilters);
        setParams(nextFilters);
      }
    },
    [searchInput, filterParams, organizationTrendsId, dataSourceHandles],
  );

  const handleBackButton = useCallback(() => {
    setSearchInput("");

    const nextFilters = {
      ...filterParams,
      view: "wordcloud",
    };

    setFilterParams(nextFilters);
    setParams({ ...nextFilters, searchString: "" });

    if (!wordCloud?.length) {
      fetchWordCloudData();
    }
  }, [filterParams, setParams, wordCloud]);

  const handleExport = useCallback(() => {
    const params = buildSearchParams({
      filters: filterParams,
      searchInput: searchInput,
      dataSourceHandles: dataSourceHandles,
      orgTrendsId: organizationTrendsId,
    });

    dispatch(messageActions.exportAdminSearch(params));
  }, [buildSearchParams, filterParams, searchInput, dispatch]);

  const [generatingReport, setGeneratingReport] = useState(false);

  const handleGenerateReport = useCallback(() => {
    const params = buildWordCloudParams({
      channelsId: filterParams.channels,
      dataSourceHandles,
      startDate: moment(filterParams.startDate).startOf("day").toISOString(),
      endDate: moment(filterParams.endDate).endOf("day").toISOString(),
      orgTrendsId: organizationTrendsId,
      direction: filterParams.direction,
    });

    if (!params) return;

    const companyName = currentUser?._embedded?.organization?.name;
    const recipientEmail = currentUser?.username;

    setGeneratingReport(true);
    dispatch(
      messageActions.generateCustomerFeedbackReport({
        ...params,
        recipientEmail,
        companyName,
      }),
    ).finally(() => setGeneratingReport(false));
  }, [
    filterParams,
    channelIds,
    dataSourceHandles,
    organizationTrendsId,
    currentUser,
    dispatch,
  ]);

  useEffect(() => {
    if (initialized.current) return;
    const hasDataSource =
      dataSourceHandles?.length > 0 && channelIds?.length > 0;

    if (!hasDataSource) return;

    initialized.current = true;

    if (derivedParams.view === "search" && derivedParams.searchString) {
      handleSearch(derivedParams.searchString);
    } else {
      fetchInitialWordCloudData();
    }
  }, [
    dataSourceHandles,
    channelIds,
    derivedParams.view,
    derivedParams.searchString,
  ]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "");
    const id = user?._embedded?.organization?.id;

    if (id) {
      dispatch(
        dataSourceHandleActions.getAll({
          organization: id,
        }),
      );
    }
  }, [dispatch]);

  return {
    channelsList,
    channelIds,
    filterParams,
    setFilterParams,
    handleSubmit,
    handleSearchField,
    searchInput,
    handleSearchOnKeyDown,
    wordCloud,
    loadingWordCloud,
    handleWordCloudWordClick,
    handlePageChange,
    exporting,
    handleExport,
    handleBackButton,
    handleGenerateReport,
    generatingReport,
  };
}
